import equal from "fast-deep-equal"
import { mapEntityRanges } from "../model/entityRanges"
import { findSlotMarkers, isWholeSlotMarker } from "../model/slots"
import type {
  CodecResult,
  FormattingTarget,
  MessageDiagnostic,
  NativeMessageEntity,
  ParsedMessage,
  ParsedMessageEntity,
  SourceTextMapping,
} from "../types"

export function diagnostic(
  code: string,
  message: string,
  path = "/text",
  sourceRange?: { offset: number; length: number },
): MessageDiagnostic {
  return {
    code,
    severity: "error",
    path,
    ...(sourceRange ? { sourceRange } : {}),
    message,
  }
}

export function failure<T>(
  code: string,
  message: string,
  path = "/text",
  sourceRange?: { offset: number; length: number },
): CodecResult<T> {
  return {
    ok: false,
    diagnostics: [diagnostic(code, message, path, sourceRange)],
  }
}

/** Internal control flow only. Public codecs always return diagnostics. */
export class ParseFailure extends Error {
  constructor(readonly diagnostic: MessageDiagnostic) {
    super(diagnostic.message)
  }
}

export function reject(
  code: string,
  message: string,
  offset: number,
  length = 1,
): never {
  throw new ParseFailure(diagnostic(code, message, "/text", { offset, length }))
}

export function attempt<T>(run: () => CodecResult<T>): CodecResult<T> {
  try {
    return run()
  } catch (error) {
    if (error instanceof ParseFailure)
      return { ok: false, diagnostics: [error.diagnostic] }
    throw error
  }
}

export function isUtf16Boundary(text: string, offset: number): boolean {
  return !(
    offset > 0 &&
    offset < text.length &&
    /[\uD800-\uDBFF]/.test(text[offset - 1]) &&
    /[\uDC00-\uDFFF]/.test(text[offset])
  )
}

export function hasValidUnicode(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i)
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = text.charCodeAt(++i)
      if (!(next >= 0xdc00 && next <= 0xdfff)) return false
    } else if (unit >= 0xdc00 && unit <= 0xdfff) return false
  }
  return true
}

export function validUrl(url: unknown): url is string {
  if (typeof url !== "string") return false
  if (isWholeSlotMarker(url)) return true
  if (
    url.includes("{{slot:") ||
    /\s/.test(url) ||
    [...url].some(
      (char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127,
    )
  )
    return false
  try {
    const parsed = new URL(url)
    return (
      /^https?:\/\//i.test(url) &&
      ["http:", "https:"].includes(parsed.protocol) &&
      !!parsed.hostname
    )
  } catch {
    return false
  }
}

export function linkEntity(
  url: string,
  offset: number,
  length: number,
  sourceOffset: number,
  sourceLength: number,
  rawDestination?: string,
): ParsedMessageEntity {
  if (
    isWholeSlotMarker(url) &&
    rawDestination !== undefined &&
    rawDestination !== url
  )
    reject(
      "ENCODED_SLOT",
      "Slot markers must appear literally in source, not as escaped markup",
      sourceOffset,
      sourceLength,
    )
  const mention = /^tg:\/\/user\?id=([1-9][0-9]*)$/.exec(url)
  if (mention)
    return { type: "mention_by_id", offset, length, userId: mention[1] }
  if (!validUrl(url))
    reject(
      "INVALID_URL",
      "Use a static HTTP(S) URL or a whole slot marker",
      sourceOffset,
      sourceLength,
    )
  return { type: "text_link", offset, length, url }
}

export function dateEntity(
  unix: string,
  format: string | undefined,
  offset: number,
  length: number,
  sourceOffset: number,
  sourceLength: number,
): ParsedMessageEntity {
  if (
    !/^-?[0-9]+$/.test(unix) ||
    !Number.isSafeInteger(Number(unix)) ||
    (format !== undefined && !/^(?:r|w?[dD]?[tT]?)$/.test(format))
  )
    reject(
      "INVALID_DATE_TIME",
      "Invalid Telegram date-time metadata",
      sourceOffset,
      sourceLength,
    )
  return {
    type: "date_time",
    offset,
    length,
    unix_time: Number(unix),
    ...(format === undefined ? {} : { date_time_format: format }),
  }
}

export const styles = new Set<string>([
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "spoiler",
])
export const quotes = new Set<string>(["blockquote", "expandable_blockquote"])
const atomicTypes = new Set<string>([
  "mention",
  "hashtag",
  "cashtag",
  "bot_command",
  "url",
  "email",
  "phone_number",
  "text_link",
  "text_mention",
  "custom_emoji",
  "mention_by_id",
  "date_time",
])

const metadataFields: Record<string, readonly string[]> = {
  pre: ["language"],
  text_link: ["url"],
  text_mention: ["user"],
  custom_emoji: ["custom_emoji_id"],
  mention_by_id: ["userId"],
  date_time: ["unix_time", "date_time_format"],
}

export function hasUnknownEntityMetadata(entity: ParsedMessageEntity): boolean {
  const keys = new Set([
    "type",
    "offset",
    "length",
    ...(metadataFields[entity.type] ?? []),
  ])
  return Object.keys(entity).some((key) => !keys.has(key))
}

export function hasUnknownEntityType(entity: ParsedMessageEntity): boolean {
  return (
    !styles.has(entity.type) &&
    !quotes.has(entity.type) &&
    !atomicTypes.has(entity.type) &&
    !["code", "pre"].includes(entity.type)
  )
}

/** Nested aliases of a metadata-free style describe the same formatting once.
 * Only discard fully covered styles, never merge links, code languages, dates,
 * opaque metadata, adjacent ranges or crossing ranges. Source spans stay intact.
 * Sorting outer ranges first also handles equal spans without recursive trees. */
function normalizeMarkupStyles(
  entities: ParsedMessageEntity[],
): ParsedMessageEntity[] {
  const ordered = entities
    .map((entity, index) => ({ entity, index }))
    .filter(
      ({ entity }) =>
        styles.has(entity.type) && !hasUnknownEntityMetadata(entity),
    )
    .sort(
      (a, b) =>
        a.entity.offset - b.entity.offset || b.entity.length - a.entity.length,
    )
  const coveredEnds = new Map<string, number>()
  const redundant = new Set<number>()
  for (const { entity, index } of ordered) {
    const end = entity.offset + entity.length
    if ((coveredEnds.get(entity.type) ?? -1) >= end) redundant.add(index)
    else coveredEnds.set(entity.type, end)
  }
  return entities.filter((_, index) => !redundant.has(index))
}

/** Shared structural checks; unknown native annotations are retained, not guessed.
 * The range helper is shape-generic at runtime and preserves all metadata; the
 * cast bridges the native signature only for parsed ID-only mention shapes. */
export function entityDiagnostics(
  text: string,
  entities: readonly ParsedMessageEntity[],
): MessageDiagnostic[] {
  const diagnostics: MessageDiagnostic[] = []
  if (!hasValidUnicode(text))
    return [
      diagnostic(
        "INVALID_UNICODE",
        "Text contains an unpaired UTF-16 surrogate",
      ),
    ]
  const geometry = mapEntityRanges(
    entities as NativeMessageEntity[],
    { from: 0, to: 0, text: "" },
    text.length,
  )
  if (!geometry.ok)
    return [
      diagnostic(
        "INVALID_ENTITY_RANGE",
        geometry.reason,
        geometry.entityIndex === undefined
          ? "/formatting/entities"
          : `/formatting/entities/${geometry.entityIndex}`,
      ),
    ]
  const markers = findSlotMarkers(text)
  entities.forEach((entity, index) => {
    const path = `/formatting/entities/${index}`
    const range = { offset: entity.offset, length: entity.length }
    if (
      !isUtf16Boundary(text, entity.offset) ||
      !isUtf16Boundary(text, entity.offset + entity.length)
    )
      diagnostics.push(
        diagnostic(
          "SPLIT_SURROGATE",
          "Entity splits a UTF-16 surrogate pair",
          path,
          range,
        ),
      )
    if (
      markers.some((marker) =>
        [entity.offset, entity.offset + entity.length].some(
          (edge) =>
            edge > marker.offset && edge < marker.offset + marker.length,
        ),
      )
    )
      diagnostics.push(
        diagnostic(
          "SPLIT_SLOT",
          "Formatting cannot split a slot marker",
          path,
          range,
        ),
      )
    if (entity.type === "text_link" && !validUrl(entity.url))
      diagnostics.push(
        diagnostic(
          "INVALID_URL",
          "Use HTTP(S) or a whole slot marker",
          `${path}/url`,
        ),
      )
    if (
      entity.type === "custom_emoji" &&
      (typeof entity.custom_emoji_id !== "string" ||
        !/^[1-9][0-9]*$/.test(entity.custom_emoji_id))
    )
      diagnostics.push(
        diagnostic(
          "INVALID_EMOJI_ID",
          "Custom emoji needs a decimal identifier",
          path,
          range,
        ),
      )
    if (entity.type === "mention_by_id" && !/^[1-9][0-9]*$/.test(entity.userId))
      diagnostics.push(
        diagnostic(
          "INVALID_USER_ID",
          "Mention needs a decimal user identifier",
          path,
          range,
        ),
      )
    if (
      entity.type === "pre" &&
      entity.language !== undefined &&
      typeof entity.language !== "string"
    )
      diagnostics.push(
        diagnostic(
          "INVALID_LANGUAGE",
          "Language must be a string",
          path,
          range,
        ),
      )
    if (
      entity.type === "date_time" &&
      (!Number.isSafeInteger(entity.unix_time) ||
        (entity.date_time_format !== undefined &&
          (typeof entity.date_time_format !== "string" ||
            !/^(?:r|w?[dD]?[tT]?)$/.test(entity.date_time_format))))
    )
      diagnostics.push(
        diagnostic(
          "INVALID_DATE_TIME",
          "Invalid date-time metadata",
          path,
          range,
        ),
      )
    if (entity.type === "text_mention") {
      const user = entity.user
      if (
        !user ||
        !Number.isSafeInteger(user.id) ||
        user.id <= 0 ||
        typeof user.is_bot !== "boolean" ||
        typeof user.first_name !== "string" ||
        !user.first_name.length
      )
        diagnostics.push(
          diagnostic(
            "INVALID_USER",
            "A native mention requires the complete User, including id, is_bot and first_name",
            `${path}/user`,
            range,
          ),
        )
      else {
        const strings = ["last_name", "username", "language_code"] as const
        const booleans = [
          "is_premium",
          "added_to_attachment_menu",
          "can_join_groups",
          "can_read_all_group_messages",
          "supports_inline_queries",
          "can_connect_to_business",
        ] as const
        const record = user as unknown as Record<string, unknown>
        if (
          strings.some(
            (key) =>
              record[key] !== undefined && typeof record[key] !== "string",
          ) ||
          booleans.some(
            (key) =>
              record[key] !== undefined && typeof record[key] !== "boolean",
          )
        )
          diagnostics.push(
            diagnostic(
              "INVALID_USER",
              "Invalid optional User metadata",
              `${path}/user`,
              range,
            ),
          )
      }
    }
    if (hasUnknownEntityType(entity))
      diagnostics.push({
        ...diagnostic(
          "OPAQUE_ENTITY",
          "Unknown annotation is preserved and cannot be converted to markup",
          path,
          range,
        ),
        severity: "warning",
      })
  })
  // Geometry was checked above. Check Telegram's additional containment rules.
  const ordered = entities
    .map((entity, index) => ({ entity, index }))
    .sort(
      (a, b) =>
        a.entity.offset - b.entity.offset ||
        b.entity.length - a.entity.length ||
        Number(quotes.has(b.entity.type)) - Number(quotes.has(a.entity.type)),
    )
  const stack: ParsedMessageEntity[] = []
  for (const { entity, index } of ordered) {
    while (
      stack.length &&
      stack[stack.length - 1].offset + stack[stack.length - 1].length <=
        entity.offset
    )
      stack.pop()
    for (const parent of stack) {
      const code =
        (["code", "pre"].includes(entity.type) && !quotes.has(parent.type)) ||
        ["code", "pre"].includes(parent.type)
      const incompatible =
        !styles.has(entity.type) &&
        !styles.has(parent.type) &&
        (!quotes.has(parent.type) || quotes.has(entity.type))
      if (code || incompatible || parent.type === entity.type) {
        diagnostics.push(
          diagnostic(
            "INVALID_ENTITY_NESTING",
            "These Telegram entities cannot overlap",
            `/formatting/entities/${index}`,
            { offset: entity.offset, length: entity.length },
          ),
        )
        break
      }
    }
    stack.push(entity)
  }
  return diagnostics
}

export class ParsedBuilder {
  text = ""
  entities: ParsedMessageEntity[] = []
  mapping: SourceTextMapping[] = []
  constructor(
    readonly source: string,
    readonly formatting: FormattingTarget,
  ) {}
  append(value: string, offset: number, length: number) {
    this.mapping.push({
      kind: "text",
      sourceRange: { offset, length },
      textRange: { offset: this.text.length, length: value.length },
    })
    this.text += value
  }
  syntax(offset: number, length: number) {
    this.mapping.push({
      kind: "syntax",
      sourceRange: { offset, length },
      textRange: { offset: this.text.length, length: 0 },
    })
  }
  destination(
    offset: number,
    length: number,
    textOffset: number,
    textLength: number,
  ) {
    this.mapping.push({
      kind: "destination",
      sourceRange: { offset, length },
      textRange: { offset: textOffset, length: textLength },
    })
  }
  finish(): CodecResult<ParsedMessage> {
    for (const marker of findSlotMarkers(this.text)) {
      const spans = this.mapping.filter(
        (span) =>
          span.kind === "text" &&
          span.textRange.offset < marker.offset + marker.length &&
          span.textRange.offset + span.textRange.length > marker.offset,
      )
      const first = spans[0]
      const last = spans[spans.length - 1]
      const rawStart =
        first.sourceRange.offset +
        (first.sourceRange.length === first.textRange.length
          ? marker.offset - first.textRange.offset
          : 0)
      const rawEnd =
        last.sourceRange.offset +
        last.sourceRange.length -
        (last.sourceRange.length === last.textRange.length
          ? last.textRange.offset +
            last.textRange.length -
            marker.offset -
            marker.length
          : 0)
      if (
        this.source.slice(rawStart, rawEnd) !==
        this.text.slice(marker.offset, marker.offset + marker.length)
      )
        return failure(
          "ENCODED_SLOT",
          "Slot markers must appear literally in source, not as escaped markup",
          "/text",
          { offset: rawStart, length: rawEnd - rawStart },
        )
    }
    this.entities = normalizeMarkupStyles(this.entities)
    const diagnostics = entityDiagnostics(this.text, this.entities)
      .filter((item) => item.severity === "error")
      .map((item) => {
        const semantic = item.sourceRange
        const spans = semantic
          ? this.mapping.filter(
              (span) =>
                span.kind === "text" &&
                span.textRange.offset < semantic.offset + semantic.length &&
                span.textRange.offset + span.textRange.length > semantic.offset,
            )
          : []
        const start = spans[0]?.sourceRange.offset ?? 0
        const last = spans[spans.length - 1]?.sourceRange
        return {
          ...item,
          path: "/text",
          sourceRange: {
            offset: start,
            length: last
              ? last.offset + last.length - start
              : this.source.length,
          },
        }
      })
    return diagnostics.length
      ? { ok: false, diagnostics }
      : {
          ok: true,
          value: {
            source: this.source,
            formatting: this.formatting,
            text: this.text,
            entities: this.entities,
            mapping: this.mapping.sort(
              (a, b) => a.sourceRange.offset - b.sourceRange.offset,
            ),
          },
        }
  }
}

export interface EntityNode {
  entity: ParsedMessageEntity
  children: EntityNode[]
}

/** Deterministic tree; quoting is outermost for equal ranges, then styles. */
export function entityTree(
  entities: readonly ParsedMessageEntity[],
): EntityNode[] {
  const priority = (entity: ParsedMessageEntity) =>
    quotes.has(entity.type)
      ? 0
      : entity.type === "underline"
        ? 1
        : styles.has(entity.type)
          ? 2
          : 3
  const ordered = [...entities].sort(
    (a, b) =>
      a.offset - b.offset ||
      b.length - a.length ||
      priority(a) - priority(b) ||
      a.type.localeCompare(b.type),
  )
  const roots: EntityNode[] = []
  const stack: EntityNode[] = []
  for (const entity of ordered) {
    while (
      stack.length &&
      stack[stack.length - 1].entity.offset +
        stack[stack.length - 1].entity.length <=
        entity.offset
    )
      stack.pop()
    const node = { entity, children: [] }
    ;(stack.length ? stack[stack.length - 1].children : roots).push(node)
    stack.push(node)
  }
  return roots
}

/** A serializer must not discard even an unrecognized extra metadata field. */
export function representability(
  parsed: ParsedMessage,
  allowed: Set<string>,
): MessageDiagnostic[] {
  const diagnostics = entityDiagnostics(parsed.text, parsed.entities).filter(
    (item) => item.severity === "error",
  )
  parsed.entities.forEach((entity, index) => {
    if (!allowed.has(entity.type) || hasUnknownEntityMetadata(entity))
      diagnostics.push(
        diagnostic(
          "LOSSY_CONVERSION",
          `Cannot represent all metadata of ${entity.type} in this format`,
          `/formatting/entities/${index}`,
        ),
      )
  })
  return sourceDiagnostics(parsed, diagnostics)
}

/** Translate semantic/annotation errors back to the actual source discriminator.
 * A synthetic edited snapshot has an empty map: do not invent old positions. */
export function sourceDiagnostics(
  parsed: ParsedMessage,
  diagnostics: MessageDiagnostic[],
): MessageDiagnostic[] {
  if (parsed.formatting.kind !== "markup") return diagnostics
  return diagnostics.map((item) => {
    const index = /^\/formatting\/entities\/(\d+)/.exec(item.path)?.[1]
    const range =
      index === undefined ? item.sourceRange : parsed.entities[Number(index)]
    const spans = range
      ? parsed.mapping.filter(
          (span) =>
            span.kind === "text" &&
            span.textRange.offset < range.offset + range.length &&
            span.textRange.offset + span.textRange.length > range.offset,
        )
      : []
    const first = spans[0]?.sourceRange
    const last = spans[spans.length - 1]?.sourceRange
    const { sourceRange: unused, ...rest } = item
    return {
      ...rest,
      path: "/text",
      ...(first && last
        ? {
            sourceRange: {
              offset: first.offset,
              length: last.offset + last.length - first.offset,
            },
          }
        : {}),
    }
  })
}

export function sameSemantics(
  a: Pick<ParsedMessage, "text" | "entities">,
  b: Pick<ParsedMessage, "text" | "entities">,
): boolean {
  const sorted = (entities: readonly ParsedMessageEntity[]) =>
    [...entities].sort(
      (x, y) =>
        x.offset - y.offset ||
        x.length - y.length ||
        x.type.localeCompare(y.type) ||
        JSON.stringify(x).localeCompare(JSON.stringify(y)),
    )
  return a.text === b.text && equal(sorted(a.entities), sorted(b.entities))
}

/** A final semantic proof prevents delimiter ambiguities from silently losing data. */
export function checkedSerialization(
  parsed: ParsedMessage,
  source: string,
  parse: (source: string) => CodecResult<ParsedMessage>,
): CodecResult<string> {
  const reloaded = parse(source)
  if (!reloaded.ok || !sameSemantics(parsed, reloaded.value))
    return failure(
      "LOSSY_CONVERSION",
      "This entity/text combination cannot be represented losslessly in the target markup",
    )
  return { ok: true, value: source }
}
